import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { api, apiWithAuth } from '../lib/api';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  FunnelIcon,
  ArrowLeftIcon,
  UserGroupIcon,
  TagIcon,
  PlayIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useGeolocation } from '../utils/location';
import toast from 'react-hot-toast';

const CategoryEmployerList = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [profiles, setProfiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([categoryName].filter(Boolean));
  const [showFilters, setShowFilters] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const profilesPerPage = 12;
  
  const { latitude, longitude } = useGeolocation();

  useEffect(() => {
    fetchCategories();
    if (user) {
      fetchUserProfile();
      checkIdentityVerification();
    }
  }, []);

  useEffect(() => {
    if (categories.length > 0) {
      fetchProfiles();
    }
  }, [selectedCategories, latitude, longitude, categories, currentPage]);

  // ユーザープロフィール取得
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const response = await authApi.get('/user-profile');
      if (response.status === 200) {
        const data = response.data;
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  // 本人確認状態チェック
  const checkIdentityVerification = async () => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const response = await authApi.get('/identity-verification/status');
      if (response.status === 200) {
        const data = response.data;
        setIdentityVerified(data?.status === 'completed');
      }
    } catch (error) {
      console.error('Error checking identity verification:', error);
    }
  };

  // カテゴリ一覧取得
  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      if (response.status === 200) {
        const data = response.data;
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // 事業者プロフィール取得
  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // 複数カテゴリフィルタ（カンマ区切り）
      if (selectedCategories.length > 0) {
        params.append('categories', selectedCategories.join(','));
      }
      
      // ユーザーの位置情報を優先、次にGPS座標
      let userLat = latitude;
      let userLng = longitude;
      
      // ログインユーザーの住所から位置情報を取得
      if (user && userProfile && userProfile.prefecture && userProfile.city) {
        try {
          const address = `${userProfile.prefecture} ${userProfile.city}`;
          const geocodeResponse = await fetch(
            `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(address)}&key=demo_key&language=ja`
          );
          if (geocodeResponse.ok) {
            const geocodeData = await geocodeResponse.json();
            if (geocodeData.results && geocodeData.results.length > 0) {
              userLat = geocodeData.results[0].geometry.lat;
              userLng = geocodeData.results[0].geometry.lng;
            }
          }
        } catch (geocodeError) {
          console.log('Geocoding failed, using GPS coordinates');
        }
      }
      
      // GPS座標をパラメータに追加
      if (userLat && userLng) {
        params.append('latitude', userLat);
        params.append('longitude', userLng);
      }

      const response = await api.get(`/employer-profiles?${params}`);
      
      if (response.status === 200) {
        let data = response.data;
        
        // キーワード検索フィルタ（フロントエンド側）
        if (searchKeyword.trim()) {
          const keyword = searchKeyword.toLowerCase();
          data = data.filter(profile => 
            profile.business_name.toLowerCase().includes(keyword) ||
            profile.description.toLowerCase().includes(keyword) ||
            profile.tags?.some(tag => tag.toLowerCase().includes(keyword))
          );
        }
        
        // ページネーション計算
        const total = data.length;
        const totalPagesCalculated = Math.ceil(total / profilesPerPage);
        setTotalPages(totalPagesCalculated);
        
        // 現在のページのデータのみ表示
        const startIndex = (currentPage - 1) * profilesPerPage;
        const endIndex = startIndex + profilesPerPage;
        const paginatedData = data.slice(startIndex, endIndex);
        
        setProfiles(paginatedData);
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
      toast.error('事業者データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // カテゴリ選択/解除
  const toggleCategory = (categoryName) => {
    setSelectedCategories(prev => 
      prev.includes(categoryName)
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  // 検索実行
  const handleSearch = () => {
    fetchProfiles();
  };

  // フィルターリセット
  const resetFilters = () => {
    setSelectedCategories([]);
    setSearchKeyword('');
  };

  const getCurrentCategory = () => {
    if (selectedCategories.length === 1) {
      return categories.find(cat => cat.name === selectedCategories[0]);
    }
    return null;
  };

  const currentCategory = getCurrentCategory();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {selectedCategories.length === 1 ? `${selectedCategories[0]}の事業者` : '事業者一覧'}
                </h1>
                {currentCategory && (
                  <p className="text-sm text-gray-600 mt-1">
                    {currentCategory.description}
                  </p>
                )}
              </div>
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              フィルター
            </button>
          </div>
        </div>
      </div>

      {/* フィルターパネル */}
      {showFilters && (
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="container mx-auto">
            {/* キーワード検索 */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                キーワード検索
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder="事業者名、説明、タグで検索"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  検索
                </button>
              </div>
            </div>

            {/* カテゴリフィルター */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                カテゴリ（複数選択可）
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {categories.map((category) => (
                  <label
                    key={category.id}
                    className={`flex items-center p-2 border rounded-lg cursor-pointer transition-all ${
                      selectedCategories.includes(category.name)
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category.name)}
                      onChange={() => toggleCategory(category.name)}
                      className="sr-only"
                    />
                    <div className="flex items-center text-sm">
                      <span className="text-lg mr-2">{category.icon}</span>
                      <span className="font-medium">{category.name}</span>
                      <span className="ml-2 text-xs text-gray-500">({category.employer_count})</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* アクションボタン */}
            <div className="flex gap-2">
              <button
                onClick={resetFilters}
                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                フィルターをリセット
              </button>
              <div className="flex-1"></div>
              <span className="text-sm text-gray-600 self-center">
                {profiles.length}件の事業者が見つかりました
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 選択中のカテゴリ表示 */}
      {selectedCategories.length > 0 && (
        <div className="bg-blue-50 border-b border-blue-200 p-4">
          <div className="container mx-auto">
            <div className="flex items-center gap-2">
              <TagIcon className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">選択中のカテゴリ:</span>
              <div className="flex flex-wrap gap-2">
                {selectedCategories.map((categoryName) => {
                  const category = categories.find(c => c.name === categoryName);
                  return (
                    <span
                      key={categoryName}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {category?.icon} {categoryName}
                      <button
                        onClick={() => toggleCategory(categoryName)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* メインコンテンツ */}
      <div className="container mx-auto px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-200 rounded-lg h-64 animate-pulse"></div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              該当する事業者が見つかりません
            </h3>
            <p className="text-gray-500 mb-6">
              {selectedCategories.length > 0 
                ? 'フィルター条件を変更して再度検索してください' 
                : '現在登録されている事業者はありません'}
            </p>
            {selectedCategories.length > 0 && (
              <button
                onClick={resetFilters}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
              >
                フィルターをリセット
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden cursor-pointer"
                onClick={() => navigate(`/employer-profile/${profile.id}`)}
              >
                {/* 事業者プロフィール画像・動画 */}
                <div className="h-48 bg-gray-200 relative overflow-hidden">
                  {/* 動画がある場合は動画を優先表示（自動再生・ミュート） */}
                  {profile.videos && profile.videos.length > 0 ? (
                    <div className="relative w-full h-full">
                      <video
                        src={`data:video/mp4;base64,${profile.videos[0]}`}
                        className="w-full h-full object-cover"
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                      <div className="absolute bottom-2 right-2">
                        <div className="bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded flex items-center">
                          <PlayIcon className="h-3 w-3 mr-1" />
                          動画
                        </div>
                      </div>
                    </div>
                  ) : profile.thumbnail_image || (profile.images && profile.images.length > 0) ? (
                    <img
                      src={`data:image/jpeg;base64,${profile.thumbnail_image || profile.images[0]}`}
                      alt={profile.business_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                      <UserGroupIcon className="h-16 w-16 text-gray-500" />
                    </div>
                  )}
                  
                  {/* カテゴリバッジ */}
                  <div className="absolute top-3 left-3">
                    {profile.categories && profile.categories.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {profile.categories.slice(0, 2).map((category, index) => (
                          <span key={index} className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                            {category}
                          </span>
                        ))}
                        {profile.categories.length > 2 && (
                          <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full">
                            +{profile.categories.length - 2}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full">
                        未分類
                      </span>
                    )}
                  </div>

                  {/* 距離表示 */}
                  {profile.distance_km && (
                    <div className="absolute top-3 right-3">
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                        {profile.distance_km}km
                      </span>
                    </div>
                  )}
                </div>

                {/* 事業者プロフィール情報 */}
                <div className="p-4">
                  {/* 事業者名 */}
                  <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-1">
                    {profile.business_name}
                  </h3>
                  
                  {/* 会社・店舗紹介の冒頭50文字 */}
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {profile.description ? 
                      (profile.description.length > 50 ? 
                        profile.description.substring(0, 50) + '...' : 
                        profile.description
                      ) : 
                      '紹介文がまだ登録されていません'}
                  </p>

                  {/* 所在地（都道府県＋市区町村） */}
                  <div className="flex items-center text-gray-500 text-sm mb-3">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    <span>{profile.prefecture} {profile.city}</span>
                  </div>

                  {/* タグ表示（3つまで） */}
                  {profile.tags && profile.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {profile.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                      {profile.tags.length > 3 && (
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          +{profile.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* ボタン */}
                  <div className="space-y-2">
                    {/* 詳細を見るボタン */}
                    <button
                      className="w-full bg-blue-500 text-white py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/employer-profile/${profile.id}`);
                      }}
                    >
                      詳細を見る
                    </button>
                    
                    {/* 応募するボタン（本人確認済みの求職者のみ表示） */}
                    {user && user.user_type === 'job_seeker' && (
                      <div>
                        {identityVerified ? (
                          <button
                            className="w-full bg-green-500 text-white py-2 rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/employer-profile/${profile.id}?action=apply`);
                            }}
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            応募する
                          </button>
                        ) : (
                          <button
                            className="w-full bg-gray-400 text-white py-2 rounded-lg font-medium cursor-not-allowed flex items-center justify-center"
                            disabled
                            title="応募するには本人確認が必要です"
                          >
                            <ClockIcon className="h-4 w-4 mr-1" />
                            本人確認が必要
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ページネーション */}
        {!loading && profiles.length > 0 && totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 mt-8">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              前へ
            </button>
            
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-lg ${
                      currentPage === pageNum
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              次へ
            </button>
            
            <div className="ml-4 text-sm text-gray-600">
              {currentPage} / {totalPages} ページ
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryEmployerList;